import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const PageHeaderExtraContext = createContext(null);

/**
 * Avvolge MainLayout: rende possibile a una pagina qualsiasi mostrare un nodo
 * accanto al titolo in SidPageHeader (stessa riga, allineato a destra),
 * invece che nel proprio corpo pagina più in basso.
 */
export const PageHeaderExtraProvider = ({ children }) => {
	const [content, setContent] = useState(null);
	const [title, setTitle] = useState(null);
	const [status, setStatus] = useState(null);
	return (
		<PageHeaderExtraContext.Provider value={{ content, setContent, title, setTitle, status, setStatus }}>
			{children}
		</PageHeaderExtraContext.Provider>
	);
};

/**
 * Da chiamare nella pagina: registra `node` nella riga del titolo. Si
 * ripulisce da solo allo smontaggio o quando `node` cambia identità.
 */
export const usePageHeaderExtra = (node) => {
	const ctx = useContext(PageHeaderExtraContext);
	useEffect(() => {
		if (!ctx) return undefined;
		ctx.setContent(node);
		return () => ctx.setContent(null);
	}, [node, ctx]);
};

// Da chiamare solo in SidPageHeader, per leggere cosa mostrare.
export const usePageHeaderExtraContent = () => {
	const ctx = useContext(PageHeaderExtraContext);
	return ctx ? ctx.content : null;
};

/**
 * Da chiamare nella pagina: sostituisce il titolo di pageMeta con uno costruito dai
 * dati (es. "Dettaglio corso: Nuoto bambini - tipo: Singolo"). Finche' `title` e'
 * vuoto resta quello di pageMeta; si ripulisce da solo allo smontaggio.
 */
export const usePageTitle = (title) => {
	const ctx = useContext(PageHeaderExtraContext);
	useEffect(() => {
		if (!ctx) return undefined;
		ctx.setTitle(title || null);
		return () => ctx.setTitle(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [title]);
};

// Da chiamare solo in SidPageHeader: titolo impostato dalla pagina, se c'e'.
export const usePageTitleOverride = () => {
	const ctx = useContext(PageHeaderExtraContext);
	return ctx ? ctx.title : null;
};

/**
 * Da chiamare nella pagina: mostra `node` subito dopo il titolo (lo stato del prodotto, vedi
 * components/Util/StatoProdotto). `node` va memoizzato: cambia solo quando cambia lo stato.
 */
export const usePageHeaderStatus = (node) => {
	const ctx = useContext(PageHeaderExtraContext);
	useEffect(() => {
		if (!ctx) return undefined;
		ctx.setStatus(node);
		return () => ctx.setStatus(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [node]);
};

// Da chiamare solo in SidPageHeader: lo stato da mostrare dopo il titolo.
export const usePageHeaderStatusContent = () => {
	const ctx = useContext(PageHeaderExtraContext);
	return ctx ? ctx.status : null;
};

/**
 * Stato in sola lettura dopo il titolo, quando non c'è un'API per cambiarlo (regola SUI).
 * `tono`: 'attivo' (ok), 'non-attivo' (non attivo, sospeso, scaduto), 'bozza' (bozza, neutro, annullato).
 * Con `label` vuota non mostra niente.
 */
export const useStatoInSolaLettura = (label, tono = 'bozza') => {
	const node = useMemo(() => (label
		? <span className={`sid-stato-chip is-${tono}`} style={{ padding: '5px 12px', cursor: 'default' }}>{label}</span>
		: null), [label, tono]);
	usePageHeaderStatus(node);
};

// Stato di un profilo (player, junior, maestro, struttura) → etichetta e tono, come nelle liste
const STATI_PROFILO = {
	ACTIVE: { label: 'Attivo', tono: 'attivo' },
	COMPLETED: { label: 'Completato', tono: 'attivo' },
	INACTIVE: { label: 'Inattivo', tono: 'non-attivo' },
	SUSPENDED: { label: 'Sospeso', tono: 'non-attivo' },
	INVITED: { label: 'Invitato', tono: 'bozza' },
};
export const statoProfilo = (code) => (code ? (STATI_PROFILO[code] || { label: code, tono: 'bozza' }) : { label: '', tono: 'bozza' });
